import { get } from 'lodash';
import { UseFormSetValue } from 'react-hook-form';
import { OpportunitiesSearchParams } from '~/contexts/types/opportunities/searchParams';
import usePagingAfter from '~/hooks/table/usePagingAfter';
import { useOpportunitiesSearchParams } from '~/hooks/useSearchParams';
import { FILTERS, RANGE_FILTERS } from '../constants';
import useSearchForm from './useSearchForm';
import { formatFromToValue } from '~/utils/formatFromToValue';
import { ORGANIZATION_TYPES } from '~/components/hook-form/constants';

type Args = {
  onSubmit: Pick<ReturnType<typeof useSearchForm>, 'onSubmit'>['onSubmit'];
  resetPage?: Pick<ReturnType<typeof usePagingAfter>, 'resetPage'>['resetPage'];
  setValue: UseFormSetValue<OpportunitiesSearchParams>;
};

export const useFilters = ({ resetPage, onSubmit, setValue }: Args) => {
  const { searchParams, defaultValues } = useOpportunitiesSearchParams();

  const getItem = (key: string) => {
    const value = get(searchParams || defaultValues, key);
    if (key === 'company.organization_type' && Array.isArray(value)) {
      const validValues = ORGANIZATION_TYPES.map((org) => org.value);
      const newValues = (value as string[]).filter((item) => validValues.includes(item));

      return newValues;
    }
    return value;
  };

  const filters = FILTERS.map((f) => ({
    item: getItem(f.key),
    name: f.title,
    keys: [f.key],
  }));

  const onDeleteFilter = (keys: string[]) => {
    keys.forEach((key: any) => setValue(key, ''));
    resetPage?.();
    onSubmit();
  };

  const rangeFilters = RANGE_FILTERS.map((f) => {
    const fromValue = get(searchParams || defaultValues, `${f.key}.from`);
    const toValue = get(searchParams || defaultValues, `${f.key}.to`);

    const value =
      !!fromValue && !!toValue
        ? `${formatFromToValue(fromValue)} – ${formatFromToValue(toValue)}`
        : !!fromValue
        ? `${formatFromToValue(fromValue)} +`
        : !!toValue
        ? `up to ${formatFromToValue(toValue)}`
        : '';

    return {
      item: value,
      name: f.title,
      keys: [`${f.key}.from`, `${f.key}.to`],
    };
  });

  return {
    onDeleteFilter,
    filters: filters.concat(rangeFilters),
  };
};
